import { type ComponentPropsWithoutRef, useState } from "react";
import { Trans } from "react-i18next";
import {
  ItemTitle,
  SeeMoreOrLessButton,
  TopicItemAccordion,
  TitleContainer,
  TitleMain,
  TitleSecondary,
  TopicDescription,
  TopicsTitle,
  OpenPopup,
} from "./styles";
import { useBreakpoint } from "hooks/use-breakpoint";
import { ArrowIcon } from "./popup/styles";
import { Popup } from "../popup";
import { ExpandButton } from "../expand-collapse-button";

export interface ItemsProps extends ComponentPropsWithoutRef<"div"> {
  title: string;
  popupTitle: string;
  topics: Item[];
}
export interface Item {
  itemTitle: string;
  shortDescription: string;
  isDisabled?: boolean;
  longDescription: string;
}

export function Items({ title, popupTitle, topics }: ItemsProps) {
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const isAllExpanded = expandedIndexes.length === topics.length;
  const [showPopup, setShowPopup] = useState(false);
  const mdBreakpoint = useBreakpoint("md");

  const handleExpandClick = (index: number) => {
    if (!mdBreakpoint) return;
    setExpandedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleAllExpansion = () => {
    if (!mdBreakpoint) return;
    setExpandedIndexes(isAllExpanded ? [] : topics.map((_, i) => i));
  };

  return (
    <>
      <TopicsTitle>
        {title}
        {mdBreakpoint ? (
          <SeeMoreOrLessButton onClick={toggleAllExpansion}>
            {isAllExpanded ? "See less" : "See more"}
          </SeeMoreOrLessButton>
        ) : (
          <OpenPopup
            onClick={() => {
              setShowPopup(true);
            }}
          >
            <ArrowIcon />
          </OpenPopup>
        )}
      </TopicsTitle>
      {topics.map((item, index) => (
        <TopicItemAccordion
          key={index}
          elevation={0}
          disableGutters
          expanded={
            mdBreakpoint && expandedIndexes.includes(index) && !item.isDisabled
          }
        >
          <ItemTitle
            {...(mdBreakpoint && {
              expandIcon: <ExpandButton disabled={item.isDisabled} />,
            })}
            aria-controls={`panel${index}-content`}
            id={`panel${index}-header`}
            disabled={item.isDisabled}
            onClick={() => {
              handleExpandClick(index);
            }}
          >
            <TitleContainer>
              <TitleMain className={item.isDisabled ? "disabled" : ""}>
                {item.itemTitle}
              </TitleMain>
              <TitleSecondary className={item.isDisabled ? "disabled" : ""}>
                {item.shortDescription}
              </TitleSecondary>
            </TitleContainer>
          </ItemTitle>
          <TopicDescription>
            <Trans>{item.longDescription}</Trans>
          </TopicDescription>
        </TopicItemAccordion>
      ))}
      {!mdBreakpoint && (
        <Popup
          content={topics}
          onClose={() => {
            setShowPopup(false);
          }}
          open={showPopup}
          title={popupTitle}
        />
      )}
    </>
  );
}
